import { useEffect, useRef, useState } from "react";
import { Drop } from "../types";
import { GOOGLE_MAPS_API_KEY } from "../data/affirmations";

interface RouteMapProps {
  drop: Drop;
  compact?: boolean;
  userLocation?: { lat: number; lng: number } | null;
  mapRef?: React.MutableRefObject<any>;
}

export function RouteMap({ drop, compact = false, userLocation = null, mapRef: externalMapRef }: RouteMapProps) {
  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const userMarkerRef = useRef<any>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const scriptId = "google-maps-script";
    if ((window as any).google) { setLoaded(true); return; }
    if (document.getElementById(scriptId)) {
      document.getElementById(scriptId)!.addEventListener("load", () => setLoaded(true));
      return;
    }
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry`;
    script.async = true;
    script.onload = () => setLoaded(true);
    script.onerror = () => setError(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!loaded || !mapDivRef.current || !(window as any).google) return;
    const google = (window as any).google;

    const center = drop.waypoints[Math.floor(drop.waypoints.length / 2)];

    const mapStyles = [
      { featureType: "all", elementType: "geometry", stylers: [{ color: "#faf7f4" }] },
      { featureType: "water", elementType: "geometry", stylers: [{ color: "#d4e8f0" }] },
      { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
      { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#f5ede8" }] },
      { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#f0d9d0" }] },
      { featureType: "park", elementType: "geometry", stylers: [{ color: "#e8f5e9" }] },
      { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
      { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
      { elementType: "labels.text.fill", stylers: [{ color: "#999999" }] },
      { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
    ];

    const map = new google.maps.Map(mapDivRef.current, {
      center: { lat: center.lat, lng: center.lng },
      zoom: compact ? 14 : 15,
      styles: mapStyles,
      disableDefaultUI: true,
      zoomControl: !compact,
      gestureHandling: compact ? "none" : "greedy",
    });

    mapInstanceRef.current = map;
    if (externalMapRef) externalMapRef.current = map;

    const directionsService = new google.maps.DirectionsService();
    const directionsRenderer = new google.maps.DirectionsRenderer({
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: drop.accentColor,
        strokeWeight: 4,
        strokeOpacity: 0.85,
      },
    });
    directionsRenderer.setMap(map);

    const origin = drop.waypoints[0];
    const destination = drop.waypoints[drop.waypoints.length - 1];
    const midpoints = drop.waypoints.slice(1, -1).map(w => ({
      location: new google.maps.LatLng(w.lat, w.lng),
      stopover: false,
    }));

    directionsService.route({
      origin: new google.maps.LatLng(origin.lat, origin.lng),
      destination: new google.maps.LatLng(destination.lat, destination.lng),
      waypoints: midpoints,
      travelMode: google.maps.TravelMode.WALKING,
    }, (result: any, status: any) => {
      if (status === "OK") directionsRenderer.setDirections(result);
    });

    drop.waypoints.forEach((wp, i) => {
      const isStart = i === 0;
      const isEnd = i === drop.waypoints.length - 1;
      new google.maps.Marker({
        position: { lat: wp.lat, lng: wp.lng },
        map,
        title: wp.label,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: isStart || isEnd ? 10 : 7,
          fillColor: isStart ? "#1a1a1a" : drop.accentColor,
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
        },
        label: compact ? undefined : {
          text: isStart ? "S" : isEnd ? "E" : `${i}`,
          color: "#ffffff",
          fontSize: "10px",
          fontWeight: "bold",
        },
      });
    });

  }, [loaded, drop]);

  useEffect(() => {
    if (!mapInstanceRef.current || !(window as any).google || !userLocation) return;
    const google = (window as any).google;

    if (userMarkerRef.current) {
      userMarkerRef.current.setPosition(userLocation);
    } else {
      userMarkerRef.current = new google.maps.Marker({
        position: userLocation,
        map: mapInstanceRef.current,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: "#4285F4",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 3,
        },
        title: "You are here",
        zIndex: 999,
      });
    }
  }, [userLocation]);

  if (error) return (
    <div style={{ height: compact ? "160px" : "320px", borderRadius: "16px", background: "#FFF0F3", border: "1px solid #F5DDD9", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "8px" }}>
      <div style={{ fontSize: "24px" }}>🗺️</div>
      <div style={{ fontSize: "12px", color: "#999", fontFamily: "'DM Sans',sans-serif" }}>Map unavailable</div>
    </div>
  );

  return (
    <div style={{ position: "relative", borderRadius: "16px", overflow: "hidden", height: compact ? "180px" : "320px", border: "1px solid #EDE8E3" }}>
      <div ref={mapDivRef} style={{ width: "100%", height: "100%" }} />
      {!loaded && (
        <div style={{ position: "absolute", inset: 0, background: "#FAF7F4", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "8px" }}>
          <div style={{ fontSize: "28px" }}>🗺️</div>
          <div style={{ fontSize: "12px", color: "#bbb", fontFamily: "'DM Sans',sans-serif" }}>Loading map...</div>
        </div>
      )}
      {!compact && (
        <div style={{ position: "absolute", bottom: "12px", left: "12px", right: "12px", background: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)", borderRadius: "12px", padding: "10px 14px" }}>
          <div style={{ fontSize: "10px", color: "#bbb", letterSpacing: "2px", textTransform: "uppercase", marginBottom: "6px", fontFamily: "'DM Sans',sans-serif" }}>route stops</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {drop.landmarks.map((l, i) => (
              <span key={i} style={{ fontSize: "11px", color: drop.accentColor, fontFamily: "'DM Sans',sans-serif", fontWeight: 500 }}>📍 {l}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
